/**
 * <product-gallery>
 *
 * Coordinates a product's main media display, the thumbnail row, and a
 * fullscreen lightbox <dialog>. Handles image, Shopify-hosted video,
 * external (YouTube/Vimeo) video, and 3D-model (preview-image fallback) media.
 *
 * Markup contract (selectors used by this component):
 *   [data-product-gallery-main]    — wrapper holding all main media items
 *   [data-product-gallery-thumb]   — thumbnail buttons (data-media-index="N")
 *   [data-product-gallery-dialog]  — the lightbox <dialog>
 *   [data-product-gallery-stage]   — inner wrapper holding lightbox items
 *   [data-product-gallery-open]    — element inside the main area that opens
 *                                    the lightbox (typically the image-button)
 *   [data-product-gallery-close]   — close button inside the dialog
 *   [data-product-gallery-prev]    — previous-image button
 *   [data-product-gallery-next]    — next-image button
 *   [data-zoomable]                — lightbox image that toggles 1x/2x on click
 *   .main-product__media-item      — one main media wrapper (data-media-index)
 *   .main-product__lightbox-item   — one lightbox media wrapper (data-media-index)
 *
 * Native <dialog>.showModal() handles focus trap + Escape natively.
 * Reduced-motion is respected via CSS.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-product-gallery-main]');
    this.mainItems = Array.from(this.querySelectorAll('.main-product__media-item'));
    this.thumbs = Array.from(this.querySelectorAll('[data-product-gallery-thumb]'));
    this.dialog = this.querySelector('[data-product-gallery-dialog]');
    this.dialogItems = this.dialog
      ? Array.from(this.dialog.querySelectorAll('.main-product__lightbox-item'))
      : [];
    this.closeBtn = this.dialog && this.dialog.querySelector('[data-product-gallery-close]');
    this.prevBtn = this.dialog && this.dialog.querySelector('[data-product-gallery-prev]');
    this.nextBtn = this.dialog && this.dialog.querySelector('[data-product-gallery-next]');

    this.currentIndex = 0;
    this.lastOpener = null;

    this.thumbs.forEach((btn) => {
      btn.addEventListener('click', () => this.show(Number(btn.dataset.mediaIndex)));
    });

    if (this.main) {
      this.main.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-product-gallery-open]');
        if (!trigger) return;
        const item = trigger.closest('.main-product__media-item');
        if (!item) return;
        this.openLightbox(Number(item.dataset.mediaIndex), trigger);
      });
    }

    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => this.onDialogClick(e));
      this.dialog.addEventListener('keydown', (e) => this.onDialogKeydown(e));
      this.dialog.addEventListener('close', () => this.onDialogClose());
    }

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.dialog.close());
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.navigate(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.navigate(1));
  }

  show(index) {
    if (Number.isNaN(index)) return;
    this.currentIndex = index;
    this.mainItems.forEach((item, i) => {
      const active = i === index;
      item.hidden = !active;
      if (!active) this.pauseMedia(item);
    });
    this.thumbs.forEach((t) => {
      const matches = Number(t.dataset.mediaIndex) === index;
      t.setAttribute('aria-current', matches ? 'true' : 'false');
    });
  }

  showInDialog(index) {
    this.currentIndex = index;
    this.dialogItems.forEach((item, i) => {
      const active = i === index;
      item.hidden = !active;
      if (!active) this.pauseMedia(item);
      const img = item.querySelector('[data-zoomable]');
      if (img) img.classList.remove('is-zoomed');
    });
  }

  openLightbox(index, opener) {
    if (!this.dialog) return;
    this.lastOpener = opener || null;
    this.showInDialog(index);
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    if (this.closeBtn) this.closeBtn.focus();
  }

  navigate(delta) {
    if (!this.dialogItems.length) return;
    const total = this.dialogItems.length;
    const next = (((this.currentIndex + delta) % total) + total) % total;
    this.showInDialog(next);
    this.show(next);
  }

  onDialogClick(e) {
    // Bail when the click was handled by a dedicated button listener.
    if (e.target.closest('[data-product-gallery-close]')) return;
    if (e.target.closest('[data-product-gallery-prev]')) return;
    if (e.target.closest('[data-product-gallery-next]')) return;

    // Zoom toggle on lightbox images.
    const img = e.target.closest('[data-zoomable]');
    if (img) {
      img.classList.toggle('is-zoomed');
      return;
    }

    // Never close on video/iframe interactions (controls live inside them).
    if (e.target.closest('video') || e.target.closest('iframe')) return;

    // Anything else inside the dialog counts as a backdrop click.
    this.dialog.close();
  }

  onDialogKeydown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.navigate(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.navigate(1);
    }
    // Escape is handled natively by <dialog>.
  }

  onDialogClose() {
    this.dialogItems.forEach((item) => {
      this.pauseMedia(item);
      // Reset external-video iframes so YouTube/Vimeo stop playing in the bg.
      const iframe = item.querySelector('iframe');
      if (iframe && iframe.src) {
        const src = iframe.src;
        iframe.src = '';
        iframe.src = src;
      }
      const img = item.querySelector('[data-zoomable]');
      if (img) img.classList.remove('is-zoomed');
    });
    if (this.lastOpener && typeof this.lastOpener.focus === 'function') {
      this.lastOpener.focus();
    }
  }

  pauseMedia(item) {
    const video = item.querySelector('video');
    if (video && typeof video.pause === 'function') video.pause();
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
